import React, { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PanelLayout from "layouts/PanelLayout";
import "styles/panel.css";

const TeacherPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [subjects, setSubjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubjects = useCallback(async (query = "") => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("Token topilmadi");

      const response = await fetch(
        "https://algoritm-api-edu.uz/api/teacher/Subjects/GetList",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            page: 1,
            pageSize: 100,
            search: query.trim(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Server xatosi: ${response.status}`);
      }

      const data = await response.json();

      const subjectsData = Array.isArray(data)
        ? data
        : data.rows || data.data || data.items || data.subjects || [];

      setSubjects(subjectsData);
    } catch (err) {
      setError(err.message || "Ma'lumotlarni yuklab bo'lmadi");
      setSubjects([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  useEffect(() => {
    const timer = setTimeout(() => fetchSubjects(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchSubjects]);

  const openSubject = (subject) => {
    const slug =
      subject.slug ||
      (subject.name || subject.fullName || "")
        .toLowerCase()
        .replace(/\s+/g, "-") ||
      `subject-${subject.id}`;

    navigate(`/teacher/subject/${slug}`, { state: subject });
  };

  const isMainTeacher = location.pathname === "/teacher";

  return (
    <PanelLayout>
      <Outlet />

      {isMainTeacher && (
        <div style={{ padding: "20px" }}>
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            style={{ marginBottom: "24px" }}
          >
            <input
              type="text"
              placeholder="Fan nomi bo'yicha qidirish..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                maxWidth: "400px",
                padding: "12px 16px",
                fontSize: "15px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                outline: "none",
                transition: "all 0.2s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#28a745";
                e.target.style.boxShadow = "0 0 0 3px rgba(40, 167, 69, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e2e8f0";
                e.target.style.boxShadow = "none";
              }}
            />
          </motion.div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                style={{
                  padding: "12px 16px",
                  background: "#fee",
                  color: "#c33",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                <strong>Xatolik:</strong> {error}
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ minHeight: "60vh", position: "relative" }}>
            <AnimatePresence mode="wait">
              {isLoading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                  }}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    style={{
                      width: 64,
                      height: 64,
                      border: "6px solid #e0e7ff",
                      borderTop: "6px solid #28a745",
                      borderRadius: "50%",
                      margin: "0 auto 16px",
                    }}
                  />
                  <p
                    style={{
                      color: "#38e360",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                  >
                    Yuklanmoqda...
                  </p>
                </motion.div>
              ) : subjects.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    position: "absolute",
                    top: "30%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "#94a3b8",
                    width: "100%",
                    maxWidth: "400px",
                  }}
                >
                  <h3 style={{ fontSize: "20px", margin: "0 0 8px" }}>
                    {searchQuery
                      ? "Hech narsa topilmadi"
                      : "Hozircha fanlar yo'q"}
                  </h3>
                  <p style={{ fontSize: "15px", color: "#64748b" }}>
                    {searchQuery
                      ? "Boshqa so'z bilan qidirib ko'ring"
                      : "Fanlar tez orada qo'shiladi"}
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="subject_card_wrapper"
                >
                  {subjects.map((subject, index) => (
                    <motion.div
                      key={subject.id || index}
                      className="subject_card"
                      onClick={() => openSubject(subject)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.06, duration: 0.4 }}
                      whileHover={{ scale: 1.03, y: -4 }}
                      whileTap={{ scale: 0.98 }}
                      style={{ cursor: "pointer" }}
                    >
                      {subject.imgUrl ? (
                        <img
                          src={`https://algoritm-api-edu.uz/${subject.imgUrl}`}
                          alt={subject.fullName || subject.name || "Fan"}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/300x200/e0e7ff/6366f1?text=No+Image";
                          }}
                          style={{
                            width: "100%",
                            height: "180px",
                            objectFit: "cover",
                            borderRadius: "12px",
                            background: "#f1f5f9",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "180px",
                            background: "#f1f5f9",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#94a3b8",
                            fontSize: "18px",
                            fontWeight: "500",
                          }}
                        >
                          Rasm yo‘q
                        </div>
                      )}

                      <div style={{ padding: "16px" }}>
                        <h4
                          style={{
                            margin: "0 0 8px",
                            fontSize: "17px",
                            fontWeight: "600",
                          }}
                        >
                          {subject.fullName || subject.name || "Nomsiz fan"}
                        </h4>
                        <p
                          style={{
                            margin: 0,
                            color: "#64748b",
                            fontSize: "14px",
                          }}
                        >
                          {subject.shortName || "Qisqacha ma'lumot yo‘q"}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </PanelLayout>
  );
};

export default TeacherPage;
